import { useCallback, useEffect, useRef, useState } from "react";
import { apiFetch } from "../api/client";
import BrowseUserIdentityLink from "./BrowseUserIdentityLink";
import SettingsSavedNotice from "./SettingsSavedNotice";

const CHAT_USERNAME_TRUNCATE_AT = 10;

function truncateChatUsername(value, truncateAt = CHAT_USERNAME_TRUNCATE_AT) {
  const normalizedValue = String(value ?? "").trim();

  if (!normalizedValue || normalizedValue.length <= truncateAt) {
    return normalizedValue;
  }

  return `${normalizedValue.slice(0, truncateAt)}...`;
}

function formatTimestamp(value) {
  if (!value) {
    return null;
  }

  const timestamp = new Date(value);

  if (Number.isNaN(timestamp.getTime())) {
    return null;
  }

  return timestamp.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short"
  });
}

function buildConversationFallback(counterparty) {
  if (!counterparty?.id) {
    return null;
  }

  return {
    counterparty: {
      id: Number(counterparty.id),
      username: counterparty.username || `User ${counterparty.id}`,
      collectorTier: counterparty.collectorTier || null,
      role: counterparty.role || null
    },
    tradeCounts: { pending: 0, accepted: 0, completed: 0, cancelled: 0 },
    trades: [],
    messages: [],
    latestMessage: null,
    lastActivityAt: null
  };
}

export default function UserConversationPanel({
  userId,
  currentUserLabel,
  counterparty,
  onOpenCounterparty,
  refreshKey,
  onConversationChanged,
  emptySelectionMessage = "Select a conversation.",
  title = "Conversation"
}) {
  const counterpartyUserId = Number(counterparty?.id);
  const [conversation, setConversation] = useState(() => buildConversationFallback(counterparty));
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [draftMessage, setDraftMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const messageListRef = useRef(null);

  const loadConversation = useCallback(async () => {
    if (!Number.isInteger(counterpartyUserId) || counterpartyUserId < 1) {
      setConversation(null);
      return;
    }

    try {
      setLoadingConversation(true);
      const response = await apiFetch(`/api/messages/conversations/${counterpartyUserId}`, { userId });
      const data = await response.json().catch(() => ({}));

      if (response.status === 404) {
        setConversation(buildConversationFallback(counterparty));
        setError(null);
        return;
      }

      if (!response.ok) {
        throw new Error(data.error || "Could not load conversation");
      }

      setConversation(data.conversation || buildConversationFallback(counterparty));
      setError(null);
    } catch (err) {
      console.error("Conversation panel load error:", err);
      setConversation(buildConversationFallback(counterparty));
      setError(err.message || "Could not load conversation");
    } finally {
      setLoadingConversation(false);
    }
  }, [counterparty, counterpartyUserId, userId]);

  useEffect(() => {
    setNotice(null);
    setDraftMessage("");
    loadConversation();
  }, [counterpartyUserId, loadConversation, refreshKey, userId]);

  useEffect(() => {
    const container = messageListRef.current;

    if (!container) {
      return;
    }

    container.scrollTop = container.scrollHeight;
  }, [counterpartyUserId, conversation?.messages?.length, loadingConversation]);

  useEffect(() => {
    if (!notice) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => setNotice(null), 3600);
    return () => window.clearTimeout(timeoutId);
  }, [notice]);

  async function handleSendMessage(event) {
    event.preventDefault();

    if (!Number.isInteger(counterpartyUserId) || counterpartyUserId < 1 || !draftMessage.trim()) {
      return;
    }

    try {
      setIsSending(true);
      setNotice(null);
      setError(null);

      const response = await apiFetch(`/api/messages/users/${counterpartyUserId}`, {
        userId,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body: draftMessage.trim() })
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || "Could not send message");
      }

      setDraftMessage("");
      setNotice("Message sent.");
      await loadConversation();
      await Promise.resolve(onConversationChanged?.(counterpartyUserId));
    } catch (err) {
      console.error("Conversation panel send error:", err);
      setError(err.message || "Could not send message");
    } finally {
      setIsSending(false);
    }
  }

  if (!Number.isInteger(counterpartyUserId) || counterpartyUserId < 1) {
    return (
      <div className="tradefinder-workspace-column tradefinder-workspace-side-panel" style={{ justifyContent: "center" }}>
        <div style={{ color: "#64748b", lineHeight: 1.7 }}>{emptySelectionMessage}</div>
      </div>
    );
  }

  return (
    <>
      <div className="tradefinder-workspace-column tradefinder-workspace-side-panel" style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateRows: "auto auto minmax(0, 1fr) auto", minHeight: "720px", maxHeight: "min(78vh, 920px)", height: "100%" }}>
          <div style={{ padding: "18px 22px 10px", borderBottom: "1px solid #e2e8f0", background: "#f8fafc" }}>
            <div style={{ fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#64748b" }}>
              {title}
            </div>
          </div>

          <div style={{ padding: "18px 22px", borderBottom: "1px solid #e2e8f0", display: "grid", gap: "10px", background: "#ffffff" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "14px", flexWrap: "wrap" }}>
              <BrowseUserIdentityLink
                asButton={true}
                onClick={() => onOpenCounterparty?.(conversation?.counterparty || counterparty)}
                username={conversation?.counterparty?.username || counterparty?.username || `User ${counterpartyUserId}`}
                tier={conversation?.counterparty?.collectorTier || counterparty?.collectorTier}
                role={conversation?.counterparty?.role || counterparty?.role}
                size="sm"
                wrap={true}
                truncateAt={CHAT_USERNAME_TRUNCATE_AT}
                style={{ justifySelf: "start" }}
              />
              <div style={{ fontSize: "12px", color: "#64748b", fontWeight: 600 }}>
                {conversation?.latestMessage?.createdAt || conversation?.lastActivityAt
                  ? `Latest activity ${formatTimestamp(conversation?.latestMessage?.createdAt || conversation?.lastActivityAt)}`
                  : "No messages yet"}
              </div>
            </div>
          </div>

          <div ref={messageListRef} style={{ padding: "22px", background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)", overflowY: "auto", display: "grid", gap: "14px", alignContent: conversation?.messages?.length ? "start" : "center" }}>
            {error ? <div className="account-error" style={{ margin: 0 }}>{error}</div> : null}

            {loadingConversation ? (
              <div className="account-loading" style={{ margin: 0 }}>Loading conversation...</div>
            ) : conversation?.messages?.length ? conversation.messages.map((message) => {
              const isOwnMessage = Number(message.senderUserId) === Number(userId);
              const senderLabel = isOwnMessage
                ? (currentUserLabel || "You")
                : (message.senderUsername || conversation?.counterparty?.username || counterparty?.username || "Counterparty");
              const displaySenderLabel = truncateChatUsername(senderLabel);

              return (
                <div
                  key={`${message.id}:${message.dealId || "direct"}`}
                  style={{
                    justifySelf: isOwnMessage ? "end" : "start",
                    maxWidth: "min(520px, 100%)",
                    padding: "14px 16px",
                    borderRadius: isOwnMessage ? "18px 18px 6px 18px" : "18px 18px 18px 6px",
                    background: isOwnMessage ? "#0f766e" : "#ffffff",
                    color: isOwnMessage ? "#ffffff" : "#0f172a",
                    border: isOwnMessage ? "1px solid #0f766e" : "1px solid #dbe4f0",
                    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
                    display: "grid",
                    gap: "8px"
                  }}
                >
                  <div
                    style={{ fontSize: "12px", fontWeight: 800, color: isOwnMessage ? "rgba(255,255,255,0.88)" : "#0f766e", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    title={displaySenderLabel !== senderLabel ? senderLabel : undefined}
                  >
                    {displaySenderLabel}
                  </div>
                  <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.6, wordBreak: "break-word" }}>
                    {message.body}
                  </div>
                  <div style={{ fontSize: "11px", color: isOwnMessage ? "rgba(255,255,255,0.72)" : "#64748b", fontWeight: 600 }}>
                    {formatTimestamp(message.createdAt)}
                  </div>
                </div>
              );
            }) : (
              <div style={{ padding: "24px", borderRadius: "18px", border: "1px dashed #cbd5e1", background: "rgba(255, 255, 255, 0.7)", color: "#475569", lineHeight: 1.7, maxWidth: "520px" }}>
                No messages in this chat yet. Send the first note to coordinate payment, shipping, pickup, or anything else related to your trades with this user.
              </div>
            )}
          </div>

          <form onSubmit={handleSendMessage} style={{ padding: "18px 22px", borderTop: "1px solid #e2e8f0", background: "#ffffff", display: "grid", gap: "10px" }}>
            <textarea
              value={draftMessage}
              onChange={(event) => setDraftMessage(event.target.value.slice(0, 1200))}
              rows={4}
              placeholder="Write a message about timing, meetup, shipping, card condition, or anything else you need to coordinate"
              style={{
                width: "100%",
                boxSizing: "border-box",
                borderRadius: "14px",
                border: "1px solid #cbd5e1",
                padding: "14px 16px",
                resize: "vertical",
                color: "#0f172a"
              }}
            />

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
              <div style={{ fontSize: "12px", color: "#64748b", fontWeight: 600 }}>
                {draftMessage.length} / 1200 characters
              </div>
              <button
                type="submit"
                disabled={isSending || !draftMessage.trim()}
                className="app-button-base"
                style={{
                  minWidth: "150px",
                  borderRadius: "12px",
                  border: "1px solid #0f766e",
                  background: "#0f766e",
                  color: "#ffffff",
                  cursor: isSending || !draftMessage.trim() ? "not-allowed" : "pointer",
                  opacity: isSending || !draftMessage.trim() ? 0.65 : 1
                }}
              >
                {isSending ? "Sending..." : "Send message"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {notice ? <SettingsSavedNotice message={notice} /> : null}
    </>
  );
}